import React, { useState, useEffect } from "react";

const SmartOrNotGame = () => {
  const [screen, setScreen] = useState("intro");
  const [score, setScore] = useState(0);
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState("");

  const [smartZoneItems, setSmartZoneItems] = useState([]);
  const [notSmartZoneItems, setNotSmartZoneItems] = useState([]);

  const smartItems = {
    "Siri": "smart",
    "ChatGPT": "smart",
    "Robot Dog": "smart",
    "Self-Driving Car": "smart",
    "Smart Vacuum": "smart",
    "Face Unlock Phone": "smart",
    "Music App": "smart",
    "Image Classifier": "smart",
  };

  const notSmartItems = {
    "Toaster": "notsmart",
    "Washing Machine": "notsmart",
    "Calculator": "notsmart",
    "Remote-Control Car": "notsmart",
    "Microwave": "notsmart",
    "Alarm Clock": "notsmart",
    "Light Switch": "notsmart",
    "Traffic Light": "notsmart",
  };

  const allItems = { ...smartItems, ...notSmartItems };
  const totalItems = Object.keys(allItems).length;

  useEffect(() => {
    const shuffled = Object.keys(allItems)
      .sort(() => Math.random() - 0.5)
      .map((name) => ({ name, type: allItems[name], locked: false }));
    setItems(shuffled);
  }, []);

  const handleDrop = (e, zone) => {
    e.preventDefault();
    const name = e.dataTransfer.getData("text/plain");

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.name === name && item.type === zone && !item.locked) {
          setScore((prev) => prev + 1);
          setFeedback(`✅ Correct! ${name} goes in the right place.`);

          if (zone === "smart") {
            setSmartZoneItems((prev) => [...prev, item]);
          } else {
            setNotSmartZoneItems((prev) => [...prev, item]);
          }

          return { ...item, locked: true };
        } else if (item.name === name && item.type !== zone && !item.locked) {
          setFeedback(`❌ Not quite! Try placing ${name} again.`);
        }
        return item;
      })
    );
  };

  const styles = {
    body: {
      fontFamily: "'Comic Sans MS', cursive",
      background: "linear-gradient(to right,rgb(238, 128, 214),rgb(32, 226, 200))",
      minHeight: "100vh",
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: "900px",
      margin: "0 auto",
      padding: "40px 20px",
    },
    title: {
      fontSize: "2.5rem",
      color: "#FF4081",
      textAlign: "center",
      marginBottom: "30px",
    },
    bubble: {
      backgroundColor: "#D1F5FF",
      borderRadius: "20px",
      padding: "20px",
      margin: "20px 0",
      fontSize: "1.2rem",
      boxShadow: "2px 2px 8px rgba(0,0,0,0.1)",
    },
    highlight: {
      color: "#D84315",
      fontWeight: "bold",
    },
    button: {
      background: "linear-gradient(to right,rgb(255, 255, 255),rgb(245, 115, 206))",
      color: "white",
      padding: "14px 30px",
      fontSize: "1.2rem",
      border: "none",
      borderRadius: "14px",
      cursor: "pointer",
      display: "block",
      margin: "40px auto 0",
    },
    h1: {
      textAlign: "center",
      // background: "linear-gradient(to right,rgb(255, 255, 255),rgb(255, 255, 255))",
      color: "black",
      padding: "20px",
      margin: 0,
      fontSize: "2rem",
    },
    score: {
      textAlign: "center",
      fontSize: "1.2rem",
      fontWeight: "bold",
      margin: "20px",
      color: "#2E7D32",
    },
    itemsContainer: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "15px",
      margin: "0 20px 30px",
    },
    item: (locked) => ({
      width: "140px",
      padding: "12px",
      backgroundColor: "#FFF8E1",
      border: "3px solid #42A5F5",
      borderRadius: "16px",
      textAlign: "center",
      cursor: locked ? "not-allowed" : "grab",
      fontSize: "1rem",
      boxShadow: "3px 3px 10px rgba(0,0,0,0.15)",
      opacity: locked ? 0.5 : 1,
      userSelect: "none",
    }),
    dropzone: {
      background: "#FFFDE7",
      border: "4px dashed #FFA726",
      borderRadius: "20px",
      width: "42%",
      minHeight: "300px",
      padding: "20px",
    },
    dropzoneTitle: {
      textAlign: "center",
      color: "#FB8C00",
      fontSize: "1.5rem",
      marginBottom: "10px",
    },
    gameArea: {
      display: "flex",
      justifyContent: "space-around",
      flexWrap: "wrap",
      padding: "0 20px 40px",
    },
    feedback: {
      textAlign: "center",
      fontSize: "1.2rem",
      fontWeight: "bold",
      marginTop: "10px",
    },
    success: {
      fontSize: "1.3rem",
      background: "#C8E6C9",
      padding: "10px",
      margin: "20px auto 0",
      borderRadius: "12px",
      width: "fit-content",
      color: "#1B5E20",
    },
  };

  return (
    <div style={styles.body}>
      {screen === "intro" ? (
        <div style={styles.container}>
          <div style={styles.title}>Activity 1: Smart or Not?</div>
          <div style={styles.bubble}>👋🏻 Ready to spot the smart machines?</div>
          <div style={styles.bubble}>
            🤖 Drag each machine into the correct box: <strong>Smart – Can Learn</strong> or <strong>Just Follows Commands</strong>.
          </div>
          <div style={styles.bubble}>
            ✅ Smart – can learn, adapt, and think.<br />
            ❌ Not smart – follows fixed instructions.
          </div>
          <button style={styles.button} onClick={() => setScreen("game")}>👉 Start the Game</button>
        </div>
      ) : (
        <div>
          <h1 style={styles.h1}>🎮 Smart or Not? Drag & Sort the AI Items!</h1>
          <div style={styles.score}>Score: {score} / {totalItems}</div>

          <div style={styles.itemsContainer}>
            {items.map((item) => (
              !item.locked && (
                <div
                  key={item.name}
                  style={styles.item(item.locked)}
                  draggable={!item.locked}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", item.name);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                >
                  {item.name}
                </div>
              )
            ))}
          </div>

          <div style={styles.gameArea}>
            <div
              style={styles.dropzone}
              onDrop={(e) => handleDrop(e, "smart")}
              onDragOver={(e) => e.preventDefault()}
            >
              <div style={styles.dropzoneTitle}> Smart – Can Learn</div>
              {smartZoneItems.map((item) => (
                <div key={item.name} style={styles.item(true)}>{item.name}</div>
              ))}
            </div>

            <div
              style={styles.dropzone}
              onDrop={(e) => handleDrop(e, "notsmart")}
              onDragOver={(e) => e.preventDefault()}
            >
              <div style={styles.dropzoneTitle}>🤖 Just Follows Commands</div>
              {notSmartZoneItems.map((item) => (
                <div key={item.name} style={styles.item(true)}>{item.name}</div>
              ))}
            </div>
          </div>

          <div style={styles.feedback}>
            {score === totalItems ? (
              <div style={styles.success}>🎉 You sorted everything correctly! You're an AI Expert!</div>
            ) : (
              feedback
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartOrNotGame;
